import React from "react";
import { Invoice } from "../../../features/groups/groupSlice";
import { MdCheckCircle, MdPending, MdError, MdCancel } from "react-icons/md";

export enum TabKeys {
  CURRENTPLAN = "current-plan",
  PLANS = "available-plans",
  INVOICES = "invoices",
}

export interface TabConfig {
  key: TabKeys;
  label: string;
  component: React.ReactNode;
}

export const URL_TAB_PARAM_KEY = 'tab'

export enum PricePeriod {
  MONTHLY = "monthly",
  YEARLY = "annually",
}

export type PlanName = "Free Tier" | "Tier 1" | "Tier 2" | "Tier 3";

export enum ChangeType {
  Starting = "starting",
  Upgrading = "upgrading",
  Downgrading = "downgrading",
}

export const isStyleFeatureAvailable = (planName: PlanName | string): boolean =>
  planName?.toLowerCase().includes("tier 3") ?? false;

export const getPlanChangeType = (
  current: string,
  selected: string
): ChangeType | null => {
  const allowedPlans: PlanName[] = ["Free Tier", "Tier 1", "Tier 2", "Tier 3"];

  if (
    !allowedPlans.includes(current as PlanName) ||
    !allowedPlans.includes(selected as PlanName)
  ) {
    throw new Error("Invalid plan name");
  }

  if (current === selected) {
    return null;
  }

  if (current === "Free Tier") {
    return ChangeType.Starting;
  }

  const planOrder: PlanName[] = ["Free Tier", "Tier 1", "Tier 2", "Tier 3"];
  const currentIndex = planOrder.indexOf(current as PlanName);
  const selectedIndex = planOrder.indexOf(selected as PlanName);

  if (selectedIndex > currentIndex) {
    return ChangeType.Upgrading;
  } else if (selectedIndex < currentIndex) {
    return ChangeType.Downgrading;
  } else {
    throw new Error("No plan change detected");
  }
};

export const invoiceCreator = (count: number): Invoice[] => {
  function randomDate(start: Date, end: Date): Date {
    return new Date(start.getTime() + Math.random() * (end.getTime() - start.getTime()));
  }

  function randomString(length: number): string {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  function createRandomInvoice(index: number): Invoice {
    const currencies = ["USD", "GBP", "EUR"];
    const statuses = ["paid", "pending", "overdue", "cancelled"];

    const invoice_id = randomString(24);
    const invoice_number = "INV-" + (1000 + index);
    const invoice_date = randomDate(new Date(2022, 0, 1), new Date());
    const invoice_url = `https://example.com/invoices/${invoice_id}`;
    const invoice_amount = Math.floor(Math.random() * 20000) + 1000;
    const invoice_currency = currencies[Math.floor(Math.random() * currencies.length)];
    const invoice_status = statuses[Math.floor(Math.random() * statuses.length)];
    const stripe_payment_intent_id = randomString(24);

    return {
      invoice_id,
      invoice_number,
      invoice_date,
      invoice_url,
      invoice_amount,
      invoice_currency,
      invoice_status,
      stripe_payment_intent_id,
    };
  }

  return Array.from({ length: count }, (_, i) => createRandomInvoice(i));
};

export const formatCurrency = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  });
  return formatter.format(amount / 100); // Assuming amount is in cents
};

export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(dateObj);
};

export const formatDateTime = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(dateObj);
};

export const InvoiceStatus = {
  PAID: "paid",
  PENDING: "pending",
  OVERDUE: "overdue",
  CANCELLED: "cancelled",
} as const;
export type InvoiceStatusType = (typeof InvoiceStatus)[keyof typeof InvoiceStatus];

export const getStatusTextColor = (status: InvoiceStatusType): string => {
  switch (status.toLowerCase()) {
    case InvoiceStatus.PAID:
      return "text-green-800";
    case InvoiceStatus.PENDING:
      return "text-yellow-800";
    case InvoiceStatus.OVERDUE:
      return "text-red-800";
    case InvoiceStatus.CANCELLED:
      return "text-gray-800";
    default:
      return "text-gray-800";
  }
};

export const getStatusBgColor = (status: InvoiceStatusType): string => {
  switch (status.toLowerCase()) {
    case InvoiceStatus.PAID:
      return "bg-green-100";
    case InvoiceStatus.PENDING:
      return "bg-yellow-100";
    case InvoiceStatus.OVERDUE:
      return "bg-red-100";
    case InvoiceStatus.CANCELLED:
      return "bg-gray-100";
    default:
      return "bg-gray-100";
  }
};

export const getStatusIcon = (status: InvoiceStatusType): React.ReactNode => {
  switch (status.toLowerCase()) {
    case InvoiceStatus.PAID:
      return React.createElement(MdCheckCircle, { className: "w-4 h-4" });
    case InvoiceStatus.PENDING:
      return React.createElement(MdPending, { className: "w-4 h-4" });
    case InvoiceStatus.OVERDUE:
      return React.createElement(MdError, { className: "w-4 h-4" });
    case InvoiceStatus.CANCELLED:
      return React.createElement(MdCancel, { className: "w-4 h-4" });
    default:
      return React.createElement(MdPending, { className: "w-4 h-4" });
  }
};

export const getStatusDescription = (status: InvoiceStatusType): string => {
  switch (status.toLowerCase()) {
    case InvoiceStatus.PAID:
      return "Payment has been successfully processed";
    case InvoiceStatus.PENDING:
      return "Payment is being processed";
    case InvoiceStatus.OVERDUE:
      return "Payment is past due";
    case InvoiceStatus.CANCELLED:
      return "Invoice has been cancelled";
    default:
      return "Unknown status";
  }
};

export const calculateTotalAmount = (invoices: any[]): number => {
  return invoices.reduce((total, invoice) => total + invoice.invoice_amount, 0);
};

export const calculateTotalByStatus = (invoices: any[], status: string): number => {
  return invoices
    .filter((invoice) => invoice.invoice_status === status)
    .reduce((total, invoice) => total + invoice.invoice_amount, 0);
};

export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    GBP: "£",
    EUR: "€",
    CAD: "C$",
    AUD: "A$",
  };
  return symbols[currency] || currency;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

//////////////////////////////////////////
///////////  DOWNLOAD UTILITIES //////////
//////////////////////////////////////////

export interface DownloadFormat {
  type: "csv" | "json";
  label: string;
  description: string;
}

export interface DateFormat {
  type: "iso" | "ddmmyyyy" | "mmddyyyy" | "yyyymmdd";
  label: string;
  description: string;
}

export interface DownloadOptions {
  format: DownloadFormat;
  dateFormat: DateFormat;
}

/**
 * Formats a date according to the selected format
 */
export const formatDateForDownload = (
  date: Date | string,
  format: DownloadOptions["dateFormat"]
): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;

  if (!dateObj || isNaN(dateObj.getTime())) {
    return "Invalid Date";
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  const seconds = String(dateObj.getSeconds()).padStart(2, "0");

  switch (format.type) {
    case "ddmmyyyy":
      return `${day}/${month}/${year}`;
    case "mmddyyyy":
      return `${month}/${day}/${year}`;
    case "yyyymmdd":
      return `${year}-${month}-${day}`;
    case "iso":
      return dateObj.toISOString();
    default:
      return `${day}/${month}/${year}`;
  }
};

/**
 * Formats currency for download
 */
export const formatCurrencyForDownload = (amount: number, currency: string): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

/**
 * Converts invoices to CSV format
 */
export const convertInvoicesToCSV = (
  invoices: Invoice[],
  options: DownloadOptions
): string => {
  const headers = [
    "Invoice Number",
    "Invoice Date",
    "Amount",
    "Currency",
    "Status",
    "Invoice ID",
    "Stripe Payment Intent ID",
    "Invoice URL",
  ];

  const rows = invoices.map((invoice) => [
    `"${invoice.invoice_number}"`,
    `"${formatDateForDownload(invoice.invoice_date, options.dateFormat)}"`,
    `"${formatCurrencyForDownload(invoice.invoice_amount, invoice.invoice_currency)}"`,
    `"${invoice.invoice_currency}"`,
    `"${invoice.invoice_status}"`,
    `"${invoice.invoice_id}"`,
    `"${invoice.stripe_payment_intent_id}"`,
    `"${invoice.invoice_url || "N/A"}"`,
  ]);

  return [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
};

/**
 * Converts invoices to JSON format
 */
export const convertInvoicesToJSON = (
  invoices: Invoice[],
  options: DownloadOptions
): string => {
  const formattedInvoices = invoices.map((invoice) => ({
    invoice_number: invoice.invoice_number,
    invoice_date: formatDateForDownload(invoice.invoice_date, options.dateFormat),
    invoice_amount: invoice.invoice_amount,
    invoice_currency: invoice.invoice_currency,
    invoice_status: invoice.invoice_status,
    invoice_id: invoice.invoice_id,
    stripe_payment_intent_id: invoice.stripe_payment_intent_id,
    invoice_url: invoice.invoice_url || null,
    formatted_amount: formatCurrencyForDownload(
      invoice.invoice_amount,
      invoice.invoice_currency
    ),
  }));

  return JSON.stringify(
    {
      export_info: {
        exported_at: new Date().toISOString(),
        total_invoices: invoices.length,
        format: options.format.type,
        date_format: options.dateFormat.type,
        date_format_description: options.dateFormat.description,
      },
      invoices: formattedInvoices,
    },
    null,
    2
  );
};

/**
 * Downloads content as a file
 */
export const downloadFile = (
  content: string,
  filename: string,
  mimeType: string
): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Downloads a single invoice
 */
export const downloadSingleInvoice = (
  invoice: Invoice,
  options: DownloadOptions
): void => {
  const timestamp = new Date().toISOString().split("T")[0];
  const baseFilename = `invoice-${invoice.invoice_number}-${timestamp}`;

  if (options.format.type === "csv") {
    const csvContent = convertInvoicesToCSV([invoice], options);
    downloadFile(csvContent, `${baseFilename}.csv`, "text/csv");
  } else {
    const jsonContent = convertInvoicesToJSON([invoice], options);
    downloadFile(jsonContent, `${baseFilename}.json`, "application/json");
  }
};

/**
 * Downloads multiple invoices
 */
export const downloadMultipleInvoices = (
  invoices: Invoice[],
  options: DownloadOptions
): void => {
  const timestamp = new Date().toISOString().split("T")[0];
  const baseFilename = `invoices-${timestamp}`;

  if (options.format.type === "csv") {
    const csvContent = convertInvoicesToCSV(invoices, options);
    downloadFile(csvContent, `${baseFilename}.csv`, "text/csv");
  } else {
    const jsonContent = convertInvoicesToJSON(invoices, options);
    downloadFile(jsonContent, `${baseFilename}.json`, "application/json");
  }
};

/**
 * Gets the appropriate MIME type for a format
 */
export const getMimeType = (format: DownloadOptions["format"]): string => {
  switch (format.type) {
    case "csv":
      return "text/csv";
    case "json":
      return "application/json";
    default:
      return "text/plain";
  }
};

/**
 * Gets the appropriate file extension for a format
 */
export const getFileExtension = (format: DownloadOptions["format"]): string => {
  switch (format.type) {
    case "csv":
      return "csv";
    case "json":
      return "json";
    default:
      return "txt";
  }
};

const capitaliseFirstCharacters = (text: string): string =>
  text
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
